/** Session-pinned chat layout — survives in-app navigation until explicit close. */
export const CHAT_LAYOUT_PINNED_KEY = "kriya-chat-layout-pinned";

/**
 * Pin page chrome (toolbars / filters) to the visible main strip beside chat.
 * ChatView sets `--main-visible-width` on the split main scrollport; body content
 * may extend under the chat, but chrome using this class stays usable.
 */
export const CHAT_VISIBLE_CHROME_CLASSNAME =
  "sticky left-0 z-30 box-border w-[var(--main-visible-width,100%)] max-w-[var(--main-visible-width,100%)] bg-background";

/** Same as {@link CHAT_VISIBLE_CHROME_CLASSNAME} for pages with `p-4` content padding. */
export const CHAT_VISIBLE_CHROME_IN_PAGE_PAD_CLASSNAME = `${CHAT_VISIBLE_CHROME_CLASSNAME} -mx-4 px-4`;

export const CHAT_LAYOUT_VIEWS = new Set(["app", "chat", "split-view"]);

export type ChatLayoutView = "app" | "chat" | "split-view";

export type PinnedChatLayoutView = "chat" | "split-view";

export function isChatLayoutView(value: string | null): value is ChatLayoutView {
  return value !== null && CHAT_LAYOUT_VIEWS.has(value);
}

export function isChatLayoutOpen(selectedView: string): boolean {
  return selectedView === "split-view" || selectedView === "chat";
}

function canUseSessionStorage(): boolean {
  return typeof globalThis.sessionStorage !== "undefined";
}

export function getPinnedChatLayout(): PinnedChatLayoutView | null {
  if (!canUseSessionStorage()) return null;
  const raw = sessionStorage.getItem(CHAT_LAYOUT_PINNED_KEY);
  if (raw === "split-view" || raw === "chat") return raw;
  return null;
}

export function pinChatLayout(view: PinnedChatLayoutView): void {
  if (!canUseSessionStorage()) return;
  sessionStorage.setItem(CHAT_LAYOUT_PINNED_KEY, view);
}

export function unpinChatLayout(): void {
  if (!canUseSessionStorage()) return;
  sessionStorage.removeItem(CHAT_LAYOUT_PINNED_KEY);
}

const PINNED_LAYOUT_EVENT = "kriya:chat-layout-pinned";
const COLLAPSE_APP_SIDEBAR_EVENT = "kriya:collapse-app-sidebar";
const EXPAND_APP_SIDEBAR_EVENT = "kriya:expand-app-sidebar";

export function subscribePinnedChatLayout(onChange: () => void): () => void {
  const handler = (): void => onChange();
  globalThis.addEventListener(PINNED_LAYOUT_EVENT, handler);
  globalThis.addEventListener("storage", handler);
  return (): void => {
    globalThis.removeEventListener(PINNED_LAYOUT_EVENT, handler);
    globalThis.removeEventListener("storage", handler);
  };
}

export function notifyPinnedChatLayoutChange(): void {
  globalThis.dispatchEvent(new Event(PINNED_LAYOUT_EVENT));
}

/** Collapse the expanded left app nav when chat opens (sidebar listens). */
export function requestCollapseAppSidebar(): void {
  globalThis.dispatchEvent(new Event(COLLAPSE_APP_SIDEBAR_EVENT));
}

export function subscribeCollapseAppSidebar(onCollapse: () => void): () => void {
  const handler = (): void => onCollapse();
  globalThis.addEventListener(COLLAPSE_APP_SIDEBAR_EVENT, handler);
  return (): void => {
    globalThis.removeEventListener(COLLAPSE_APP_SIDEBAR_EVENT, handler);
  };
}

/** Expand the left app nav so tour targets (e.g. product selector) are visible. */
export function requestExpandAppSidebar(): void {
  globalThis.dispatchEvent(new Event(EXPAND_APP_SIDEBAR_EVENT));
}

export function subscribeExpandAppSidebar(onExpand: () => void): () => void {
  const handler = (): void => onExpand();
  globalThis.addEventListener(EXPAND_APP_SIDEBAR_EVENT, handler);
  return (): void => {
    globalThis.removeEventListener(EXPAND_APP_SIDEBAR_EVENT, handler);
  };
}

export function setPinnedChatLayout(view: PinnedChatLayoutView | null): void {
  const current = getPinnedChatLayout();
  if (view === current) {
    return;
  }
  if (view === null) {
    unpinChatLayout();
  } else {
    pinChatLayout(view);
  }
  notifyPinnedChatLayoutChange();
}

function readCurrentSearchParams(
  currentSearch?: string,
): URLSearchParams {
  if (typeof currentSearch === "string") {
    return new URLSearchParams(
      currentSearch.startsWith("?") ? currentSearch.slice(1) : currentSearch,
    );
  }
  if (typeof globalThis.window === "undefined") {
    return new URLSearchParams();
  }
  return new URLSearchParams(globalThis.window.location.search);
}

function isOpenChatLayoutView(value: string | null): boolean {
  return value === "split-view" || value === "chat";
}

/**
 * Carry the active chat thread across in-app navigations.
 * Without this, product/project hops drop `?threadId=` and the next send
 * creates an empty thread (looks like LLM amnesia).
 */
export function withPinnedChatThreadQuery(
  searchParams: URLSearchParams | Readonly<URLSearchParams>,
  currentSearch?: string,
): URLSearchParams {
  const params = new URLSearchParams(searchParams.toString());
  if (params.get("threadId")) {
    return params;
  }

  // Explicitly closing chat — do not keep the thread on the destination.
  if (params.get("view") === "app") {
    return params;
  }

  const chatOpen =
    isOpenChatLayoutView(params.get("view")) || getPinnedChatLayout() !== null;
  if (!chatOpen) {
    return params;
  }

  const currentThreadId = readCurrentSearchParams(currentSearch)
    .get("threadId")
    ?.trim();
  if (currentThreadId && currentThreadId !== "default") {
    params.set("threadId", currentThreadId);
  }

  return params;
}

/** Merge pinned chat layout into query params when navigation omits `view`. */
export function withPinnedChatLayoutQuery(
  searchParams: URLSearchParams | Readonly<URLSearchParams>,
): URLSearchParams {
  const params = new URLSearchParams(searchParams.toString());
  const urlView = params.get("view");
  const pinned = getPinnedChatLayout();

  if (urlView === "app") {
    return params;
  }

  if (isChatLayoutView(urlView) && urlView !== "app") {
    return params;
  }

  if (pinned) {
    params.set("view", pinned);
  }

  return params;
}

/**
 * Preserve pinned chat `view` + active `threadId` when building in-app
 * navigation targets so product ↔ project browsing keeps the same chat.
 */
export function hrefPreservingPinnedChatLayout(
  href: string,
  currentSearch?: string,
): string {
  const hashIndex = href.indexOf("#");
  const withoutHash = hashIndex >= 0 ? href.slice(0, hashIndex) : href;
  const hash = hashIndex >= 0 ? href.slice(hashIndex) : "";
  const queryIndex = withoutHash.indexOf("?");
  const pathname =
    queryIndex >= 0 ? withoutHash.slice(0, queryIndex) : withoutHash;
  const query =
    queryIndex >= 0 ? withoutHash.slice(queryIndex + 1) : "";
  const withView = withPinnedChatLayoutQuery(new URLSearchParams(query));
  const params = withPinnedChatThreadQuery(withView, currentSearch);
  const qs = params.toString();
  return qs.length > 0 ? `${pathname}?${qs}${hash}` : `${pathname}${hash}`;
}
